import type {
  DataTypeClass,
  DistributionHint,
  PropertyConstraints,
  PropertySpec,
  TwinDataSpec,
} from '../types/spec';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inferDataType(schema: string): DataTypeClass {
  const s = schema.toLowerCase();
  if (s === 'double' || s === 'float' || s === 'long') return 'numerical_continuous';
  if (s === 'integer') return 'numerical_discrete';
  if (s === 'boolean') return 'boolean';
  if (s === 'datetime' || s === 'date' || s === 'time' || s === 'duration')
    return 'datetime';
  if (s === 'string') return 'text';
  return 'categorical';
}

function defaultConstraints(
  dataType: DataTypeClass,
  _schema: string,
  enumValues?: string[]
): PropertyConstraints {
  const base: PropertyConstraints = { nullable: false, distribution: 'none' as DistributionHint };
  switch (dataType) {
    case 'numerical_continuous':
      return { ...base, min: 0, max: 100, distribution: 'uniform' };
    case 'numerical_discrete':
      return { ...base, min: 0, max: 100, step: 1, distribution: 'uniform' };
    case 'categorical':
      return { ...base, lookupValues: enumValues ?? [], distribution: 'categorical_uniform' };
    case 'boolean':
      return { ...base };
    case 'datetime':
      return { ...base, minDate: '2020-01-01', maxDate: '2026-12-31' };
    case 'text':
      return { ...base, maxLength: 256 };
    default:
      return base;
  }
}

function resolveSchema(schema: unknown): { label: string; enumValues?: string[] } {
  if (typeof schema === 'string') return { label: schema };
  if (typeof schema === 'object' && schema !== null) {
    const obj = schema as Record<string, unknown>;
    if (obj['@type'] === 'Enum') {
      const vals = (obj.enumValues as Array<Record<string, unknown>> | undefined)?.map(
        (v) => String(v.name ?? v.enumValue ?? '')
      );
      return { label: 'Enum', enumValues: vals };
    }
    if (obj['@type'] === 'Object') return { label: 'Object' };
    if (obj['@type'] === 'Map') return { label: 'Map' };
    if (obj['@type'] === 'Array') return { label: 'Array' };
    return { label: JSON.stringify(schema) };
  }
  return { label: String(schema) };
}

function resolveContentType(type: unknown): string {
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) return type[0] ?? 'Property';
  return 'Property';
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

export interface ParseResult {
  spec: TwinDataSpec;
  warnings: string[];
}

export function parseDtdlAndTwin(
  dtdlRaw: string,
  twinRaw: string
): ParseResult {
  const warnings: string[] = [];

  let dtdl: Record<string, unknown>;
  try {
    const parsed = JSON.parse(dtdlRaw);
    dtdl = Array.isArray(parsed) ? parsed[0] : parsed;
  } catch (e) {
    throw new Error(`Invalid DTDL JSON: ${(e as Error).message}`);
  }

  const modelId = String(dtdl['@id'] ?? '');
  const rawModelDisplayName = dtdl.displayName;
  let modelDisplayName: string;
  if (typeof rawModelDisplayName === 'string') {
    modelDisplayName = rawModelDisplayName;
  } else if (typeof rawModelDisplayName === 'object' && rawModelDisplayName !== null) {
    modelDisplayName = String((rawModelDisplayName as Record<string, string>).en ?? modelId);
  } else {
    modelDisplayName = modelId;
  }

  const contents = (dtdl.contents as Array<Record<string, unknown>>) ?? [];

  let twin: Record<string, unknown> = {};
  if (twinRaw.trim()) {
    try {
      twin = JSON.parse(twinRaw);
    } catch (e) {
      warnings.push(`Twin JSON parse warning: ${(e as Error).message}. Twin values will be empty.`);
    }
  }

  const twinId = String(twin.$dtId ?? twin.$id ?? '');

  const properties: PropertySpec[] = contents
    .filter((item) => {
      const name = String(item.name ?? '');
      if (!name) return false;
      const contentType = resolveContentType(item['@type']);
      return contentType !== 'Relationship' && contentType !== 'Component';
    })
    .map((item) => {
      const name = String(item.name ?? '');
      const contentType = resolveContentType(item['@type']);
      const { label: schemaLabel, enumValues } = resolveSchema(item.schema);
      const dataType = inferDataType(schemaLabel);
      const constraints = defaultConstraints(dataType, schemaLabel, enumValues);

      const rawItemDisplayName = item.displayName;
      let displayName: string;
      if (typeof rawItemDisplayName === 'string') {
        displayName = rawItemDisplayName;
      } else if (typeof rawItemDisplayName === 'object' && rawItemDisplayName !== null) {
        displayName = String((rawItemDisplayName as Record<string, string>).en ?? name);
      } else {
        displayName = name;
      }

      const twinValue = twin[name] ?? null;

      if (twinValue !== null && twinValue !== undefined) {
        if (dataType === 'numerical_continuous' || dataType === 'numerical_discrete') {
          const v = Number(twinValue);
          if (!Number.isNaN(v)) {
            if (constraints.min !== undefined && v < constraints.min) {
              constraints.min = Math.floor(v - Math.abs(v) * 0.5);
            }
            if (constraints.max !== undefined && v > constraints.max) {
              constraints.max = Math.ceil(v + Math.abs(v) * 0.5);
            }
          }
        }
      }

      return {
        name,
        displayName,
        dtdlSchema: schemaLabel,
        dtdlContentType: contentType,
        twinValue,
        dataType,
        constraints,
        included: true,
      };
    });

  return {
    spec: { modelId, twinId, modelDisplayName, properties },
    warnings,
  };
}
